import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/Animations';
import { McIcon } from '../components/CraftingGrid';
import './WikiItemDetail.css';
import api from '../API/Api';

const VIDEO = "https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/background_vids/vid_1.mp4";
const LOOT_PREVIEW = 5;

function qtyLabel(item) {
  const min = item.qty_min != null ? Number(item.qty_min) : null;
  const max = item.qty_max != null ? Number(item.qty_max) : null;
  if (min == null) return null;
  if (max != null && max !== min) return `${min}–${max}`;
  return `×${min}`;
}

function LootChip({ item, rarityColor }) {
  const isCustom = item.item_type === "custom";
  const qty = qtyLabel(item);
  const chip = (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: "0.5rem",
      background: isCustom ? `${rarityColor}0d` : "rgba(255,255,255,0.03)",
      border: `1px solid ${isCustom ? `${rarityColor}44` : "rgba(255,255,255,0.09)"}`,
      borderRadius: "0.45rem", padding: "0.42rem 0.75rem",
      textDecoration: "none",
      cursor: item.wiki_link ? "pointer" : "default",
      transition: "background 0.13s, border-color 0.13s",
    }}>
      <McIcon materialKey={item.item_icon || "chest"} size={16} />
      <span style={{ fontSize: "0.82rem", fontWeight: isCustom ? 600 : 400, color: isCustom ? rarityColor : "var(--text-secondary)" }}>
        {item.item_name}
      </span>
      {qty && (
        <>
          <span style={{ width: 1, height: 12, background: isCustom ? `${rarityColor}44` : "rgba(255,255,255,0.1)", flexShrink: 0 }} />
          <span style={{ fontSize: "0.72rem", fontWeight: 700, color: isCustom ? rarityColor : "var(--text-muted)", opacity: 0.9 }}>
            {qty}
          </span>
        </>
      )}
      {isCustom && (
        <span style={{
          fontSize: "0.58rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em",
          color: rarityColor, background: `${rarityColor}22`, border: `1px solid ${rarityColor}44`,
          borderRadius: "0.25rem", padding: "0.08rem 0.35rem", lineHeight: 1.4,
        }}>
          custom
        </span>
      )}
    </div>
  );
  return item.wiki_link
    ? <Link to={item.wiki_link} style={{ textDecoration: "none" }}>{chip}</Link>
    : chip;
}

function dropLabel(chance) {
  if (chance == null) return null;
  if (chance >= 100) return { label: "Guaranteed",   color: "#4caf50" };
  if (chance >= 50)  return { label: "Common Drop",   color: "#8bc34a" };
  if (chance >= 25)  return { label: "Uncommon Drop", color: "#c8b800" };
  if (chance >= 10)  return { label: "Rare Drop",     color: "#2196f3" };
  if (chance >= 5)   return { label: "Epic Drop",     color: "#9c27b0" };
  if (chance >= 1)   return { label: "Legendary Drop","color": "#ff9800" };
  return               { label: "Mythic Drop",     color: "#f44336" };
}

// Used inside the modal where we show full item rows with drop chance
function LootRow({ item, rarityColor }) {
  const isCustom = item.item_type === "custom";
  const qty = qtyLabel(item);
  const chance = item.drop_chance != null ? parseFloat(item.drop_chance) : null;
  const dl = dropLabel(chance);
  const inner = (
    <div style={{
      display: "flex", alignItems: "center", gap: "0.75rem",
      padding: "0.55rem 0.75rem",
      background: isCustom ? `${rarityColor}08` : "transparent",
      border: `1px solid ${isCustom ? `${rarityColor}33` : "rgba(255,255,255,0.06)"}`,
      borderRadius: "0.45rem",
      textDecoration: "none",
      transition: "background 0.13s",
    }}>
      <McIcon materialKey={item.item_icon || "chest"} size={18} />
      <span style={{ flex: 1, fontSize: "0.85rem", fontWeight: isCustom ? 600 : 400, color: isCustom ? rarityColor : "var(--text-secondary)" }}>
        {item.item_name}
      </span>
      {qty && (
        <span style={{ fontSize: "0.75rem", fontWeight: 700, color: isCustom ? rarityColor : "var(--text-muted)", opacity: 0.85, whiteSpace: "nowrap" }}>
          {qty}
        </span>
      )}
      {isCustom && (
        <span style={{
          fontSize: "0.58rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em",
          color: rarityColor, background: `${rarityColor}22`, border: `1px solid ${rarityColor}44`,
          borderRadius: "0.25rem", padding: "0.08rem 0.35rem", lineHeight: 1.4, whiteSpace: "nowrap",
        }}>
          custom
        </span>
      )}
      {dl && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.1rem", flexShrink: 0 }}>
          <span style={{
            fontSize: "0.7rem", fontWeight: 700,
            color: dl.color, background: `${dl.color}1a`,
            border: `1px solid ${dl.color}55`,
            borderRadius: "0.3rem", padding: "0.1rem 0.45rem", whiteSpace: "nowrap",
          }}>
            {dl.label}
          </span>
          <span style={{ fontSize: "0.62rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>
            {chance}% chance
          </span>
        </div>
      )}
    </div>
  );
  return item.wiki_link
    ? <Link to={item.wiki_link} style={{ textDecoration: "none", display: "block" }}>{inner}</Link>
    : inner;
}

function RarityDropsModal({ rarity, onClose }) {
  const loot = rarity.loot ?? [];
  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}
      onClick={onClose}
    >
      <div
        style={{ background: "var(--bg-card)", border: `1px solid ${rarity.color}44`, borderRadius: "1rem", padding: "1.5rem", width: "100%", maxWidth: 620, maxHeight: "80vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: "1rem" }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: rarity.color, boxShadow: `0 0 8px ${rarity.color}88` }} />
            <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: rarity.color }}>{rarity.name} Drops</h3>
            <div style={{ display: "flex", alignItems: "center", gap: "0.25rem", background: `${rarity.color}18`, border: `1px solid ${rarity.color}44`, borderRadius: "2rem", padding: "0.12rem 0.55rem" }}>
              <span style={{ fontSize: "0.85rem", fontWeight: 800, color: rarity.color }}>{rarity.chance}%</span>
              <span style={{ fontSize: "0.6rem", color: `${rarity.color}aa`, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>chance</span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--text-muted)", fontSize: "1.4rem", cursor: "pointer", lineHeight: 1, padding: "0 0.25rem" }}>×</button>
        </div>
        {loot.length === 0 ? (
          <div style={{ fontSize: "0.85rem", color: "var(--text-muted)", fontStyle: "italic" }}>No loot configured yet.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
            {loot.map((item, i) => <LootRow key={i} item={item} rarityColor={rarity.color} />)}
          </div>
        )}
      </div>
    </div>
  );
}

export default function WikiEvents() {
  const navigate = useNavigate();
  const [config, setConfig]   = useState(null);
  const [rarities, setRarities] = useState([]);
  const [loading, setLoading]  = useState(true);
  const [rarityModal, setRarityModal] = useState(null);

  useEffect(() => {
    api('getwikiseason').then(res => {
      if (res.isSuccess) {
        setConfig(res.data.config);
        setRarities(res.data.rarities ?? []);
      }
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div className="wiki-detail-root" style={{ paddingTop: "8rem", textAlign: "center", color: "var(--text-muted)" }}>
        Loading…
      </div>
    );
  }

  const supplyStatus  = config?.supply_drop_status ?? "active";
  const supplyStatusColor = { active: "#1aaf12", upcoming: "#c8b800", disabled: "#7a7a7a" }[supplyStatus] ?? "#1aaf12";
  const supplyStatusLabel = { active: "Active", upcoming: "Upcoming", disabled: "Disabled" }[supplyStatus] ?? "Active";

  return (
    <div className="wiki-detail-root">
      <div className="wiki-detail-hero">
        <video className="wiki-detail-hero-vid" src={VIDEO} autoPlay muted loop playsInline />
        <div className="wiki-detail-hero-overlay" />
        <FadeIn className="wiki-detail-hero-content container" y={30} duration={0.8}>
          <button className="wiki-back-btn" onClick={() => navigate(-1)}>← Back</button>
          <div className="wiki-detail-title-section">
            <div className="wiki-detail-icon-box wmd-icon-box" style={{ '--tier-color': '#c8b800' }}>
              <McIcon materialKey="chest" size={56} />
            </div>
            <div>
              <h1 className="wiki-detail-item-name" style={{ color: "#c8b800" }}>
                Server Events
              </h1>
              <div className="wiki-detail-item-type" style={{ color: "#c8b800" }}>
                Season {config?.season_number ?? 1} · Active Events
              </div>
            </div>
          </div>
        </FadeIn>
      </div>

      <StaggerContainer className="container">
        <div className="wiki-detail-grid">

          {/* ── Main ── */}
          <StaggerItem className="wiki-detail-main">

            {/* Supply Drop overview */}
            <div>
              <span className="wiki-detail-label">Supply Drops</span>
              <div style={{
                background: "rgba(200,184,0,0.06)", border: "1px solid rgba(200,184,0,0.3)",
                borderRadius: "0.75rem", padding: "1.25rem",
                display: "flex", flexDirection: "column", gap: "0.85rem",
              }}>
                {/* Status + schedule */}
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                  <div style={{
                    display: "flex", alignItems: "center", gap: "0.5rem",
                    background: `${supplyStatusColor}20`, border: `1px solid ${supplyStatusColor}`,
                    borderRadius: "2rem", padding: "0.3rem 0.85rem",
                  }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: supplyStatusColor, boxShadow: `0 0 5px ${supplyStatusColor}`, display: "inline-block" }} />
                    <span style={{ color: supplyStatusColor, fontWeight: 700, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      {supplyStatusLabel}
                    </span>
                  </div>
                  {config?.supply_drop_schedule && (
                    <span style={{ color: "var(--text-muted)", fontSize: "0.78rem", display: "flex", alignItems: "center", gap: "0.35rem" }}>
                      <McIcon materialKey="clock" size={13} /> {config.supply_drop_schedule}
                    </span>
                  )}
                </div>

                <p style={{ margin: 0, color: "var(--text-secondary)", lineHeight: 1.65, fontSize: "0.88rem" }}>
                  {config?.supply_drop_description || "Supply crates drop at random locations across the map. Be the first to reach one and claim the loot inside."}
                </p>

                {/* Compact rarity pills */}
                {rarities.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", paddingTop: "0.2rem" }}>
                    {[...rarities].sort((a, b) => parseFloat(b.chance) - parseFloat(a.chance)).map(r => (
                      <span key={r.id} style={{
                        display: "flex", alignItems: "center", gap: "0.35rem",
                        background: `${r.color}14`, border: `1px solid ${r.color}44`,
                        borderRadius: "2rem", padding: "0.18rem 0.6rem",
                        fontSize: "0.75rem", color: r.color, fontWeight: 600,
                      }}>
                        <span style={{ width: 5, height: 5, borderRadius: "50%", background: r.color, flexShrink: 0 }} />
                        {r.name} — {r.chance}%
                      </span>
                    ))}
                  </div>
                )}

              </div>
            </div>

            {/* Per-rarity loot cards — highest chance first */}
            {rarities.length > 0 && (
              <div>
                <span className="wiki-detail-label">Possible Loot by Rarity</span>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                  {[...rarities].sort((a, b) => parseFloat(b.chance) - parseFloat(a.chance)).map(r => {
                    const loot = r.loot ?? [];
                    const preview = loot.slice(0, LOOT_PREVIEW);
                    const hasMore = loot.length > LOOT_PREVIEW;
                    return (
                      <div key={r.id} className="wiki-detail-box" style={{ padding: "0.9rem 1rem" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: loot.length ? "0.65rem" : 0, flexWrap: "wrap", gap: "0.4rem" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                            <div style={{ width: 9, height: 9, borderRadius: "50%", background: r.color, boxShadow: `0 0 7px ${r.color}88`, flexShrink: 0 }} />
                            <span style={{ fontWeight: 700, fontSize: "0.88rem", color: r.color }}>{r.name}</span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.28rem", background: `${r.color}18`, border: `1px solid ${r.color}44`, borderRadius: "2rem", padding: "0.15rem 0.6rem" }}>
                            <span style={{ fontSize: "0.92rem", fontWeight: 800, color: r.color }}>{r.chance}%</span>
                            <span style={{ fontSize: "0.6rem", color: `${r.color}aa`, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>chance</span>
                          </div>
                        </div>
                        {loot.length === 0 ? (
                          <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", fontStyle: "italic" }}>No loot configured yet.</div>
                        ) : (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "center" }}>
                            {preview.map((item, i) => <LootChip key={i} item={item} rarityColor={r.color} />)}
                            {hasMore && (
                              <button
                                onClick={() => setRarityModal(r)}
                                style={{
                                  display: "inline-flex", alignItems: "center", gap: "0.3rem",
                                  background: `${r.color}14`, border: `1px solid ${r.color}44`,
                                  borderRadius: "0.4rem", padding: "0.32rem 0.6rem",
                                  color: r.color, fontSize: "0.75rem", fontWeight: 700,
                                  cursor: "pointer", fontFamily: "inherit",
                                }}
                              >
                                +{loot.length - LOOT_PREVIEW} more · See all drops
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {rarityModal && <RarityDropsModal rarity={rarityModal} onClose={() => setRarityModal(null)} />}

          </StaggerItem>

          {/* ── Sidebar ── */}
          <StaggerItem className="wiki-detail-sidebar">

            <div>
              <span className="wiki-detail-label">All Events</span>
              <div className="wiki-detail-box" style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "0.6rem" }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: supplyStatusColor, marginTop: 5, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "0.88rem" }}>Supply Drops</div>
                    <div style={{ fontSize: "0.78rem", color: "var(--text-secondary)", marginTop: "0.15rem" }}>
                      {config?.supply_drop_schedule || "Scheduled"}
                    </div>
                  </div>
                </div>
                <div style={{ height: 1, background: "var(--border)" }} />
                <div style={{ display: "flex", alignItems: "flex-start", gap: "0.6rem", opacity: 0.4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#555", marginTop: 5, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "0.88rem", color: "var(--text-muted)" }}>More events coming</div>
                    <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: "0.15rem" }}>Season 1</div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <span className="wiki-detail-label">Related Item</span>
              <Link to="/wiki/item/SupplyDrop" style={{ textDecoration: "none" }}>
                <div className="wiki-detail-box" style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.85rem 1rem" }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}>
                  <McIcon materialKey="beacon" size={28} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "0.88rem", color: "#ff9800" }}>Supply Drop</div>
                    <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>Legendary Utility · View item ↗</div>
                  </div>
                </div>
              </Link>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              <Link to="/wiki/season"   style={{ color: "var(--teal)", textDecoration: "none", fontSize: "0.9rem" }}>→ Season Info</Link>
              <Link to="/wiki/items"    style={{ color: "var(--teal)", textDecoration: "none", fontSize: "0.9rem" }}>→ Browse All Items</Link>
              <Link to="/wiki/updates"  style={{ color: "var(--teal)", textDecoration: "none", fontSize: "0.9rem" }}>→ View Updates</Link>
            </div>

          </StaggerItem>
        </div>
      </StaggerContainer>
    </div>
  );
}
